import type {SafeHTMLString} from '@github-ui/safe-html'
import {type PropsWithChildren, createContext, useContext, useMemo, useState} from 'react'

type TitleContextProps = {
  /**
   * The main heading of the list item. Used for the aria-label of the list item and displayed visually.
   */
  title: SafeHTMLString
  setTitle: (newTitle: SafeHTMLString | string) => void
}

const TitleContext = createContext<TitleContextProps | undefined>(undefined)

export const TitleProvider = ({children}: PropsWithChildren) => {
  const [title, setTitle] = useState('' as SafeHTMLString)
  const contextProps = useMemo(
    () => ({title, setTitle: newTitle => setTitle(newTitle.trim() as SafeHTMLString)}) satisfies TitleContextProps,
    [title],
  )
  return <TitleContext.Provider value={contextProps}>{children}</TitleContext.Provider>
}

export const useListItemTitle = () => {
  const context = useContext(TitleContext)
  if (!context) throw new Error('useListItemTitle must be used with TitleProvider.')
  return context
}

try{ TitleContext.displayName ||= 'TitleContext' } catch {}
try{ TitleProvider.displayName ||= 'TitleProvider' } catch {}